/**
 * useLocationSearch
 * Shared logic for live location search suggestions using the new
 * AutocompleteSuggestion API (AutocompleteService is deprecated as of March 1st, 2025)
 */
import { ref } from 'vue';
import { debounce } from 'lodash';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('useLocationSearch');

export function useLocationSearch() {
  const suggestions     = ref([]);
  const showSuggestions = ref(false);
  const isSearching     = ref(false);

  let _suppressSync        = false;
  let _sessionToken        = null;

  // Fetch live predictions as user types using the new AutocompleteSuggestion API
  const fetchSuggestions = debounce(async (query, bounds = null) => {
    if (!query?.trim() || _suppressSync) {
      suggestions.value     = [];
      showSuggestions.value = false;
      isSearching.value     = false;
      return;
    }
    
    if (!window.google?.maps?.places?.AutocompleteSuggestion) return;
    
    isSearching.value = true;
    
    // Create a session token if we don't have one
    if (!_sessionToken) {
      _sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    }
    
    const request = {
      input: query,
      sessionToken: _sessionToken,
    };
    
    if (bounds) request.locationBias = bounds;

    try {
      const { suggestions: results } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      
      isSearching.value = false;
      if (results?.length) {
        suggestions.value = results
          .filter(s => s.placePrediction)
          .map(s => ({
            placeId:   s.placePrediction.placeId,
            main:      s.placePrediction.mainText?.toString() || '',
            secondary: s.placePrediction.secondaryText?.toString() || '',
            full:      s.placePrediction.text?.toString() || '',
            _prediction: s.placePrediction,
          }));
        showSuggestions.value = true;
      } else {
        suggestions.value     = [];
        showSuggestions.value = false;
      }
    } catch (error) {
      isSearching.value = false;
      suggestions.value     = [];
      showSuggestions.value = false;
      logger.error('Autocomplete suggestion error', error);
    }
  }, 250);

  // Resolve a placeId to full place details using the new Place API
  const resolvePlace = async (placeId, prediction = null) => {
    try {
      // Use toPlace() from prediction if available, otherwise create from ID
      const place = prediction
        ? prediction.toPlace()
        : new window.google.maps.places.Place({ id: placeId });
      
      await place.fetchFields({
        fields: ['displayName', 'location', 'formattedAddress', 'addressComponents'],
      });
      
      // Reset session token after place is resolved (ends the session)
      _sessionToken = null;
      
      if (!place.location) return null;
      
      const ac = place.addressComponents || [];
      const get = type => ac.find(c => c.types.includes(type))?.longText || '';
      
      return {
        lat: place.location.lat(),
        lng: place.location.lng(),
        viewport: place.viewport || null,
        formatted: place.formattedAddress || '',
        name: place.displayName || '',
        label: place.displayName && place.displayName !== place.formattedAddress
          ? `${place.displayName}, ${place.formattedAddress}`.replace(/,\s*$/, '')
          : place.formattedAddress || '',
        street_address: [get('street_number'), get('route')].filter(Boolean).join(' '),
        city: get('locality') || get('postal_town') || get('administrative_area_level_2') || get('administrative_area_level_1'),
        state: get('administrative_area_level_1'),
        zip_code: get('postal_code'),
        country: get('country'),
      };
    } catch (error) {
      logger.error('Error fetching place details', error);
      return null;
    }
  };

  const clearSuggestions = () => {
    suggestions.value     = [];
    showSuggestions.value = false;
  };

  const setSuppressSync = (val) => { _suppressSync = val; };

  return {
    suggestions,
    showSuggestions,
    isSearching,
    fetchSuggestions,
    resolvePlace,
    clearSuggestions,
    setSuppressSync,
  };
}
